import { useLoomControlSurfaceContext, type LoomControlSurfaceContextValue } from "./LoomControlSurfaceContext";
import type { LoomControlSurfaceViewerReceipt } from "./useLoomControlSurfaceViewerScene";
import "./loom-control-surfaces.css";

export function loomControlSurfaceViewerDataAttributes(
  context: LoomControlSurfaceContextValue | null,
  receipt: LoomControlSurfaceViewerReceipt | null,
) {
  const selectedControlId = context?.selectedControl?.id;
  const selectedState = selectedControlId ? receipt?.appliedAnglesDeg[selectedControlId] : undefined;
  const evaluation = context?.evaluation;
  return {
    "data-selected-body-id": context?.selectedBodyId ?? "none",
    "data-selected-control-id": context?.selectedControl?.id ?? "none",
    "data-control-local-sign-convention": "te-down-positive",
    "data-control-definition-revision": receipt?.definitionRevision ?? "unavailable",
    "data-control-pose-revision": receipt?.poseRevision ?? "unavailable",
    "data-control-evaluation-requested-definition-revision": evaluation?.requestedDefinitionRevision ?? "none",
    "data-control-evaluation-accepted-definition-revision": evaluation?.acceptedDefinitionRevision ?? "none",
    "data-control-evaluation-requested-pose-revision": evaluation?.requestedPoseRevision ?? "none",
    "data-control-evaluation-accepted-pose-revision": evaluation?.acceptedPoseRevision ?? "none",
    "data-control-stale-evaluation-drop-count": evaluation?.staleDropCount ?? 0,
    "data-fixed-wing-build-count": receipt?.fixedWingBuildCount ?? 0,
    "data-fixed-wing-body-count": receipt?.fixedBodyCount ?? 0,
    "data-control-body-count": receipt?.bodyCount ?? 0,
    "data-control-hinge-count": receipt?.hingeCount ?? 0,
    "data-control-topology-build-count": receipt?.topologyBuildCount ?? 0,
    "data-control-pose-apply-count": receipt?.poseApplyCount ?? 0,
    "data-fixed-wing-buffer-signature": receipt?.fixedBodyBufferSignature ?? "unavailable",
    "data-fixed-wing-geometry-signature": receipt?.fixedBodyBufferSignature ?? "unavailable",
    "data-fixed-wing-object-signature": receipt?.fixedBodyObjectSignature ?? "unavailable",
    "data-fixed-wing-transform-signature": receipt?.fixedBodyTransformSignature ?? "unavailable",
    "data-control-pose-signature": receipt?.poseRevision ?? "unavailable",
    "data-control-visualization-authority": receipt?.visualizationAuthority ?? "unavailable",
    "data-control-visual-treatment": receipt?.visualTreatment ?? "unavailable",
    "data-port-angle-deg": selectedState?.port ?? "none",
    "data-starboard-angle-deg": selectedState?.starboard ?? "none",
  };
}

export function LoomControlSurfaceViewerEvidence({ receipt }: { receipt: LoomControlSurfaceViewerReceipt | null }) {
  const context = useLoomControlSurfaceContext();
  const collection = context?.collection; const pose = context?.pose;
  if (!context || !collection || !pose) return null;
  const fixedId = `${collection.wingId}:fixed`;
  const evaluation = context.evaluation;
  const accepted = Boolean(receipt
    && evaluation.requestedDefinitionRevision === evaluation.acceptedDefinitionRevision
    && evaluation.acceptedDefinitionRevision === receipt.definitionRevision
    && evaluation.requestedPoseRevision === evaluation.acceptedPoseRevision
    && evaluation.acceptedPoseRevision === receipt.poseRevision);
  const downstream = accepted && receipt ? {
    schema: "aero.control-surface-pose.v1",
    definitionRevision: receipt.definitionRevision,
    poseRevision: receipt.poseRevision,
    states: receipt.appliedStates.map((state) => ({ wingId: collection.wingId, ...state })),
  } : null;
  return <div
    aria-label="Aircraft body selection"
    className="loom-control-viewer-evidence"
    data-fidelity="adapted-sampled-section-loft-not-openvsp-exact"
    data-geometry-operation="complementary-section-split-not-cad-boolean"
    data-source-authority={context.acceptedGeometry?.sourceAuthority ?? "unavailable"}
    data-visualization-authority="server-evaluated-ephemeral-complementary-bodies"
    data-testid="loom-control-viewer-evidence"
    role="group"
  >
    <button
      aria-label="Select fixed wing body"
      aria-pressed={context.selectedBodyId === fixedId}
      data-body-id={fixedId}
      data-pickable="true"
      data-selected={String(context.selectedBodyId === fixedId)}
      data-testid="loom-fixed-wing-body"
      data-buffer-signature={receipt?.fixedBodyBufferSignature ?? "unavailable"}
      data-object-signature={receipt?.fixedBodyObjectSignature ?? "unavailable"}
      data-transform-signature={receipt?.fixedBodyTransformSignature ?? "unavailable"}
      data-visible="true"
      data-visual-treatment="ephemeral-server-evaluated-fixed-wing-cut"
      onClick={() => context.selectBody(fixedId)}
      type="button"
    >Fixed wing</button>
    <pre
      data-authority="ephemeral-server-evaluated-definition-browser-rigid-pose-not-saved"
      data-testid="loom-control-downstream-payload"
      hidden
    >{JSON.stringify(downstream)}</pre>
    {collection.controlSurfaces.flatMap((control) => {
      return (["port", "starboard"] as const).flatMap((side) => {
        const bodyId = `${collection.wingId}:control:${control.id}:${side}`;
        const anchor = receipt?.bodyCanvasAnchors[bodyId];
        const angle = receipt?.appliedAnglesDeg[control.id]?.[side];
        const sideLabel = side === "port" ? "Port" : "Starboard";
        return <button
          aria-label={`Select ${control.label} ${side} body`}
          aria-pressed={context.selectedBodyId === bodyId}
          data-body-id={bodyId}
          data-canvas-x={anchor?.x.toFixed(2)}
          data-canvas-y={anchor?.y.toFixed(2)}
          data-control-id={control.id}
          data-pickable="true"
          data-selected={String(context.selectedBodyId === bodyId)}
          data-side={side}
          data-testid={`loom-control-body-${control.id}-${side}`}
          data-transform-signature={angle === undefined ? "unavailable" : `rigid:${angle.toFixed(6)}`}
          data-visible={String(Boolean(receipt))}
          data-visual-treatment="ephemeral-server-evaluated-movable-body"
          key={bodyId}
          onClick={() => context.selectBody(bodyId)}
          title={angle === undefined ? `${control.label} · ${sideLabel}` : `${control.label} · ${sideLabel} · ${angle.toFixed(1)}°`}
          type="button"
        >{control.label} · {sideLabel}</button>;
      });
    })}
  </div>;
}
